'use client'

import { Breadcrumb } from '@repo/ui/Breadcrumb'
import { ClientWorkspaceNavigation } from '@repo/ui/ClientWorkspaceNavigation'
import { ClaimsDrawer } from '@repo/ui/ClaimsDrawer'
import { DashboardVideoModal } from '@repo/dashboard/DashboardVideoModal'
import { EmailCompositionPanel } from '@repo/ui/EmailCompositionPanel'
import { KeyDatesDrawer } from '@repo/key-dates-drawer/KeyDatesDrawer'
import { MainNavigationMenu } from '@repo/ui/MainNavigationMenu'
import { MatterDrawer } from '@repo/ui/MatterDrawer'
import { MattersWorkspaceNavigation } from '@repo/ui/MattersWorkspaceNavigation'
import { NavigationSidebar } from '@repo/ui/NavigationSidebar'
import { NavigationSidebarModeSwitcher } from '@repo/ui/NavigationSidebarModeSwitcher'
import { NotesButton } from '@repo/ui/NotesButton'
import { NotesDrawer } from '@repo/ui/NotesDrawer'
import { Outlet, useLocation, useParams, useRouteLoaderData } from 'react-router-dom'
import { PageTitleBlock } from '@repo/ui/PageTitleBlock'
import { routesByMenuItemId, routesByPath, allRoutesByMenuItemId } from '@repo/spa_osprey/routes'
import { SiteHeader } from '@repo/ui/SiteHeader'
import { StatusBar } from '@repo/ui/StatusBar'
import { ThirdPartyNotificationDrawer } from '@repo/ui/ThirdPartyNotificationDrawer'
import { TimeRecordingModule } from '@repo/time-recording/TimeRecordingModule'
import { ToastCenter } from '@repo/ui/ToastCenter'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useEffect } from 'react'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import { useWorkflowStatusStore } from '@repo/store/useWorkflowStatusStore'
import { VatableDisbursementsModal } from '@repo/ui/VatableDisbursementsModal'
import { WizardBase } from '@repo/ui/WizardBase'
import { useNavBlockerForClientMatterNo } from '@repo/app-assets/useNavBlockerForClientMatterNo'
import MUIBox from '@mui/material/Box'
import PropTypes from 'prop-types'

export const DefaultLayout = ({ children }) => {
  const { currentMatter } = useMattersStore()
  const { fetchedWorkflowStatusPermissions, fetchWorkflowStatusPermissions } = useWorkflowStatusStore()
  const { getMenuItems } = useMenuItemsStore()
  const { handleUrlUpdate, routeId } = useUrlStore()
  const { isLoggedIn } = useAuthenticationStore()
  const { isPageScrollable, sidebarMode, pageTitleBlockIsVisible, companyCanUseMicrosoftEmail, useMicrosoftEmail } = useGlobalSettingsStore()
  const { pageIsMatterSpecific, pageIsClientSpecific, hideStatusBar } = useRouteLoaderData(routeId) || {}
  const { clientMatterNo } = currentMatter || {}
  const location = useLocation()
  const params = useParams()
  const path = location.pathname
  const leftOffset = sidebarMode === 'expanded' ? '264px' : '64px'
  useNavBlockerForClientMatterNo()

  useEffect(() => {
    clientMatterNo && fetchWorkflowStatusPermissions({ clientMatterNo })
  }, [clientMatterNo, fetchWorkflowStatusPermissions])

  useEffect(() => {
    handleUrlUpdate(params)

    getMenuItems({ routesByMenuItemId, routesByPath, allRoutesByMenuItemId })
  }, [handleUrlUpdate, path, params, getMenuItems])

  useEffect(() => {
    document.querySelector('#reactroot').style = `overflow-y: ${isPageScrollable ? 'auto' : 'hidden'}`
  }, [isPageScrollable])

  if (path.includes('/auth/') || !isLoggedIn) return null

  const calculateMaxHeight = () => {
    let maxHeight = pageTitleBlockIsVisible ? 240 : 140

    if (pageIsMatterSpecific) {
      maxHeight = pageTitleBlockIsVisible ? 420 : 290
    }

    if (pageIsClientSpecific) {
      maxHeight = pageTitleBlockIsVisible ? 340 : 210
    }

    return `${maxHeight}px`
  }

  return (
    <MUIBox sx={{ maxHeight: calculateMaxHeight(), position: 'relative' }}>
      <NavigationSidebar />
      <SiteHeader />
      {pageIsMatterSpecific || pageIsClientSpecific ? (
        <MUIBox sx={{ position: 'fixed', left: `calc(100% - ${leftOffset} - 4px)`, top: '85px', zIndex: 100 }}>
          <NotesButton level={pageIsClientSpecific ? 'Client' : 'Matter'} />
        </MUIBox>
      ) : null}
      <MUIBox
        sx={{
          marginTop: '70px', // offset for header
          marginLeft: leftOffset, // offset for sidebar
          width: `calc(100% - ${leftOffset})`,
          height: 'calc(100% - 90px)',
          minHeight: '600px',
          transition: 'all 250ms'
        }}
      >
        <NavigationSidebarModeSwitcher />
        <DashboardVideoModal />

        {companyCanUseMicrosoftEmail && useMicrosoftEmail ? <EmailCompositionPanel /> : null}
        {
          // children are passed on error pages only - if there are no children, we should render the page title and router Outlet
        }
        {children || (
          <>
            <Breadcrumb />
            <PageTitleBlock />

            {pageIsClientSpecific && <ClientWorkspaceNavigation />}
            {pageIsMatterSpecific && <MattersWorkspaceNavigation />}
            {pageIsMatterSpecific && !hideStatusBar && fetchedWorkflowStatusPermissions.canGet && <StatusBar />}
            <Outlet />
          </>
        )}
      </MUIBox>

      <MainNavigationMenu />
      <MatterDrawer />
      <ClaimsDrawer />
      <KeyDatesDrawer />
      <NotesDrawer level={pageIsMatterSpecific ? 'Matter' : pageIsClientSpecific ? 'Client' : null} />
      <ThirdPartyNotificationDrawer />
      <VatableDisbursementsModal />
      <WizardBase />
      <ToastCenter />
      <TimeRecordingModule />
    </MUIBox>
  )
}

DefaultLayout.propTypes = {
  children: PropTypes.node,
  iframeVisible: PropTypes.bool
}
